import React from 'react';
import moment from 'moment';
import _, { toNumber } from 'lodash';
import languages from './languages';
import getPluralRule from './getPluralRule';
import getLanguageDirection from './getLanguageDirection';
import { DEFAULT_LANGUAGE } from './constants';
export default function makeTranslationHelpers(currentLanguage, locales) {
    const direction = getLanguageDirection(currentLanguage);
    const pluralRule = getPluralRule(currentLanguage);
    const translations = {
        [currentLanguage]: [...extractLocales(locales, currentLanguage)],
        [DEFAULT_LANGUAGE]: [...extractLocales(locales, DEFAULT_LANGUAGE)],
    };
    const helpNoteIndexes = buildHelpNoteIndexes(locales, currentLanguage);
    return {
        t: translate.bind(null, translations, pluralRule, {
            postfix: `[${currentLanguage.toUpperCase()}]`,
            hasLangPostfix: currentLanguage !== DEFAULT_LANGUAGE,
            currentLanguage,
        }),
        formatShortDate: formatShortDate.bind(null, translations),
        getHelpNoteTooltipTitle: (originalText, params) => getHelpNoteTooltipTitle(translations, currentLanguage, pluralRule, helpNoteIndexes, originalText, params),
        currentLanguage,
        direction,
        isLayoutRTL: direction === 'rtl',
    };
}
function extractLocales(locales, language) {
    return locales
        .filter(locale => !!locale[language])
        .map(locale => locale[language]);
}
function formatShortDate(translations, date) {
    if (!date) {
        return '';
    }
    return moment(date).format('ll');
}
const hasText = (value) => typeof value === 'string' && value.trim().length > 0;
const isRecord = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
function getHelpNotes(metaValue) {
    if (!metaValue || typeof metaValue !== 'object' || Array.isArray(metaValue)) {
        return {
            original: '',
            translated: '',
        };
    }
    return {
        original: metaValue.helpNoteOriginal || '',
        translated: metaValue.helpNoteTranslated || '',
    };
}
function hasHelpNoteContent(metaValue) {
    if (!isRecord(metaValue)) {
        return false;
    }
    return (hasText(metaValue.helpNoteOriginal) || hasText(metaValue.helpNoteTranslated));
}
function buildMetaByOriginalKey(locales, currentLanguage) {
    const metaByOriginalKey = new Map();
    const currentMetaSchemaKey = `${currentLanguage}-meta`;
    for (const locale of locales) {
        if (!isRecord(locale)) {
            continue;
        }
        const metaSchemaKeys = Object.keys(locale).filter(key => key.endsWith('-meta'));
        for (const metaSchemaKey of metaSchemaKeys) {
            const metaSchema = locale === null || locale === void 0 ? void 0 : locale[metaSchemaKey];
            if (!isRecord(metaSchema)) {
                continue;
            }
            for (const [originalKey, rawMetaValue] of Object.entries(metaSchema)) {
                const metaValue = rawMetaValue;
                if (!isRecord(metaValue)) {
                    continue;
                }
                const existingMetaValue = metaByOriginalKey.get(originalKey);
                const existingMeta = isRecord(existingMetaValue) && !Array.isArray(existingMetaValue)
                    ? existingMetaValue
                    : {};
                const mergedMeta = {
                    ...existingMeta,
                };
                if (!hasText(mergedMeta.helpNoteOriginal) &&
                    hasText(metaValue.helpNoteOriginal)) {
                    mergedMeta.helpNoteOriginal = metaValue.helpNoteOriginal;
                }
                if (metaSchemaKey === currentMetaSchemaKey &&
                    hasText(metaValue.helpNoteTranslated)) {
                    mergedMeta.helpNoteTranslated = metaValue.helpNoteTranslated;
                }
                metaByOriginalKey.set(originalKey, mergedMeta);
            }
        }
    }
    return metaByOriginalKey;
}
function buildOriginalKeyByTranslatedLabel(locales, currentLanguage, metaByOriginalKey) {
    const candidateKeysByLabel = new Map();
    const originalKeyByTranslatedLabel = new Map();
    for (const locale of locales) {
        const schema = locale === null || locale === void 0 ? void 0 : locale[currentLanguage];
        if (!isRecord(schema)) {
            continue;
        }
        for (const [key, value] of Object.entries(schema)) {
            if (typeof value !== 'string' || !hasText(value)) {
                continue;
            }
            let candidateKeys = candidateKeysByLabel.get(value);
            if (!candidateKeys) {
                candidateKeys = new Set();
                candidateKeysByLabel.set(value, candidateKeys);
            }
            candidateKeys.add(key);
        }
    }
    for (const [label, candidateKeys] of candidateKeysByLabel.entries()) {
        if (candidateKeys.size === 1) {
            originalKeyByTranslatedLabel.set(label, [...candidateKeys][0]);
            continue;
        }
        const candidatesWithHelpNotes = [...candidateKeys].filter(key => hasHelpNoteContent(metaByOriginalKey.get(key)));
        originalKeyByTranslatedLabel.set(label, candidatesWithHelpNotes.length === 1 ? candidatesWithHelpNotes[0] : null);
    }
    return originalKeyByTranslatedLabel;
}
function buildHelpNoteIndexes(locales, currentLanguage) {
    const metaByOriginalKey = buildMetaByOriginalKey(locales, currentLanguage);
    return {
        metaByOriginalKey,
        originalKeyByTranslatedLabel: buildOriginalKeyByTranslatedLabel(locales, currentLanguage, metaByOriginalKey),
        tooltipTitleCache: new Map(),
    };
}
function getHelpNoteTooltipTitle(translations, currentLanguage, pluralRule, helpNoteIndexes, originalText, params) {
    if (!hasText(originalText)) {
        return originalText;
    }
    const cacheKey = params && Object.keys(params).length
        ? `${originalText}\0${JSON.stringify(params)}`
        : originalText;
    const cachedTooltipTitle = helpNoteIndexes.tooltipTitleCache.get(cacheKey);
    if (cachedTooltipTitle) {
        return cachedTooltipTitle;
    }
    const label = translate(translations, pluralRule, {
        currentLanguage,
        hasLangPostfix: false,
    }, originalText, params);
    let metaValue = helpNoteIndexes.metaByOriginalKey.get(originalText);
    if (!metaValue) {
        const resolvedOriginalKey = helpNoteIndexes.originalKeyByTranslatedLabel.get(originalText) || null;
        if (resolvedOriginalKey) {
            metaValue = helpNoteIndexes.metaByOriginalKey.get(resolvedOriginalKey);
        }
    }
    if (!metaValue || typeof metaValue !== 'object' || Array.isArray(metaValue)) {
        helpNoteIndexes.tooltipTitleCache.set(cacheKey, label);
        return label;
    }
    const { original: helpNoteOriginal, translated: helpNoteTranslated, } = getHelpNotes(metaValue);
    const preferredHelpNote = currentLanguage === DEFAULT_LANGUAGE
        ? helpNoteOriginal
        : helpNoteTranslated;
    const fallbackHelpNote = currentLanguage === DEFAULT_LANGUAGE ? '' : helpNoteOriginal;
    const helpNote = preferredHelpNote || fallbackHelpNote;
    if (!hasText(helpNote)) {
        helpNoteIndexes.tooltipTitleCache.set(cacheKey, label);
        return label;
    }
    const tooltipTitle = `${label}\n\n${helpNote}`;
    helpNoteIndexes.tooltipTitleCache.set(cacheKey, tooltipTitle);
    return tooltipTitle;
}
function addPostfix(translation, options) {
    if (!options || !translation || typeof translation !== 'string') {
        return translation;
    }
    const { postfix, hasLangPostfix } = options;
    return hasLangPostfix ? `${translation} ${postfix}` : translation;
}
const isNumberString = value => {
    const n = _.toNumber(value);
    return !_.isNull(value) && _.isFinite(n) && !_.isNaN(n);
};
function getLocaleTag(lang) {
    const localeMap = {
        ar: 'ar-EG-u-nu-arab',
        en: 'en-US-u-nu-latn',
        fr: 'fr-FR-u-nu-latn',
        fa: 'fa-IR-u-nu-arabext',
        pt: 'pt-PT-u-nu-latn',
        es: 'es-ES-u-nu-latn',
    };
    return localeMap[lang] || 'en-US-u-nu-latn';
}
function translate(translations, pluralRule, options, format, params) {
    if (Array.isArray(format)) {
        const formatMessage = format[0];
        if (options && options.currentLanguage) {
            const currentLangTranslations = translations[options.currentLanguage];
            for (const translation of currentLangTranslations) {
                if (translation[formatMessage]) {
                    format[0] = translation[formatMessage];
                }
            }
        }
        return addPostfix(translate(translations, pluralRule, null, ...format), options);
    }
    if (options && isNumberString(format)) {
        return new Intl.NumberFormat(getLocaleTag(options.currentLanguage)).format(toNumber(format));
    }
    let currentLanguageFormat;
    if (options && options.currentLanguage) {
        const currentLangTranslations = translations[options.currentLanguage];
        for (const translation of currentLangTranslations) {
            if (translation[format]) {
                currentLanguageFormat = translation[format];
                break;
            }
        }
    }
    if (currentLanguageFormat) {
        return addPostfix(interpolate(translations, pluralRule, currentLanguageFormat, { ...params }, {
            ...options,
        }), {
            ...options,
            hasLangPostfix: false,
        });
    }
    let defaultLanguageFormat;
    for (const translation of translations[DEFAULT_LANGUAGE]) {
        if (translation[format]) {
            defaultLanguageFormat = translation[format];
            break;
        }
    }
    return addPostfix(interpolate(translations, pluralRule, defaultLanguageFormat || format, { ...params }, { ...options }), {
        ...options,
    });
}
function interpolateParams(translations, pluralRule, localFormat, params, options) {
    return localFormat.replace(/#\{([\w][\w\d_]*)\}/g, (match, key) => {
        const paramValue = key in params ? params[key] : key;
        if (shouldTranslateParamValue(paramValue)) {
            return translate(translations, pluralRule, { ...options, hasLangPostfix: false }, extractParamValueToTranslate(paramValue));
        }
        return paramValue;
    });
}
function interpolate(translations, pluralRule, localFormat, params, options) {
    // console.log('interpolate');
    if (_.isString(localFormat)) {
        return interpolateParams(translations, pluralRule, localFormat, params, options);
    }
    else if (React.isValidElement(localFormat)) {
        return localFormat;
    }
    else if (_.isObject(localFormat)) {
        const rule = pluralRule(params.count);
        return interpolateParams(translations, pluralRule, localFormat[rule] || '', params, options);
    }
    return localFormat;
}
export const getLanguage = (preferences) => preferences && preferences.UI_LANGUAGE && languages[preferences.UI_LANGUAGE]
    ? preferences.UI_LANGUAGE
    : DEFAULT_LANGUAGE;
const ParamValueToTranslateRegex = /^\[(.+)\]$/;
function shouldTranslateParamValue(value) {
    return Boolean(ParamValueToTranslateRegex.exec(value));
}
function extractParamValueToTranslate(value) {
    const match = ParamValueToTranslateRegex.exec(value);
    return (match && match[1]) || '';
}
